import * as z from "zod";

export const loginValidation = z.object({
  username: z.string().min(2, { message: "Too Short" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 charachters" }),
});

export const customerValidation = z.object({
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string().optional(),
});

export const categoryValidation = (isEdit: boolean) => z.object({
  nameEn: z.string().min(2, { message: "Name in English is required" }),
  nameAr: z.string().min(2, { message: "Name in Arabic is required" }),
  iconFile: isEdit
    ? z.array(z.instanceof(File)).optional()
    : z.array(z.instanceof(File)).min(1, { message: "Icon is required" }),
  parent: z.string().optional(),
  enable: z.boolean().optional(),
});

export const featureValidation = z.object({
  nameEn: z.string().min(2, { message: "Name in English is required" }),
  nameAr: z.string().min(2, { message: "Name in Arabic is required" }),
  type: z.string().min(2, { message: "Type is required" }),
});


export const blogValidation = (isEdit: boolean) => z.object({
  titleEn: z.string().min(2, { message: "Title in English is required" }),
  titleAr: z.string().min(2, { message: "Title in Arabic is required" }),
  contentEn: z.string().min(2, { message: "Content in English is required" }),
  contentAr: z.string().min(2, { message: "Content in Arabic is required" }),
  image: isEdit
    ? z.array(z.instanceof(File)).optional()
    : z.array(z.instanceof(File)).min(1, { message: "Image is required" }),
});



export const couponValidation = z.object({
  code: z.string().min(2, { message: "Code is required" }),
  percent: z.number().min(1, { message: "Percent is required" }).max(100, { message: "Percent must be less than 100" }),
  startDate: z.date()
    .refine((date) => date >= new Date(new Date().setHours(0, 0, 0, 0)), {
      message: "Start date cannot be in the past"
    }),
  endDate: z.date(),
  isPublic: z.string().optional(),
  customers: z.array(z.string()).optional(),
}).superRefine((data, ctx) => {
  // Was `endDate < startDate` - allowed endDate === startDate through, and the
  // backend stores/displays dates at minute precision, so anything under a
  // minute apart is effectively zero duration too: a coupon that can never
  // actually be selected once saved (2026-09-06 incident, twice).
  const MIN_DURATION_MS = 5 * 60 * 1000;
  if (data.endDate.getTime() - data.startDate.getTime() < MIN_DURATION_MS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "End date must be at least 5 minutes after start date",
      path: ["endDate"]
    });
  }
});

export const companyValidation = (isEdit: boolean) => z.object({
  nameEn: z.string().min(2, { message: "Name in English is required" }),
  nameAr: z.string().min(2, { message: "Name in Arabic is required" }),
  url: z.string().min(2, { message: "Url is required" }),
  image: isEdit
    ? z.array(z.instanceof(File)).optional()
    : z.array(z.instanceof(File)).min(1, { message: "Image is required" }),
});


export const sliderValidation = (isEdit: boolean) => z.object({
  titleEn: z.string().min(2, { message: "Title in English is required" }),
  titleAr: z.string().min(2, { message: "Title in Arabic is required" }),
  descriptionEn: z.string().min(2, { message: "Description in English is required" }),
  descriptionAr: z.string().min(2, { message: "Description in Arabic is required" }),
  url: z.string().min(2, { message: "Url is required" }),
  image: isEdit
    ? z.array(z.instanceof(File)).optional()
    : z.array(z.instanceof(File)).min(1, { message: "Image is required" }),
});

export const offerValidation = z.object({
  nameEn: z.string().min(2, { message: "Name in English is required" }),
  nameAr: z.string().min(2, { message: "Name in Arabic is required" }),
  percent: z.number().min(1, { message: "Percent is required" }).max(100, { message: "Percent must be less than 100" }),
  startDate: z.date()
    .refine((date) => date >= new Date(new Date().setHours(0, 0, 0, 0)), {
      message: "Start date cannot be in the past"
    }),
  endDate: z.date(),
  type: z.enum(["PRODUCT", "CATEGORY"], { message: "Type is required" }),
  products: z.array(z.any()).min(1, { message: "Items are required" }),
}).superRefine((data, ctx) => {
  if (data.endDate < data.startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "End date cannot be before start date",
      path: ["endDate"]
    });
  }
});


export const roleValidation = z.object({
  nameEn: z.string().min(2, { message: "Name in English is required" }),
  nameAr: z.string().min(2, { message: "Name in Arabic is required" }),
});


export const userValidation = (isEdit: boolean) => z.object({
  username: z.string().min(2, { message: "Username is required" }),
  fullName: z.string().min(2, { message: "Full name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  password: isEdit
    ? z.string().optional()
    : z.string()
      .min(8, { message: "Password must be at least 8 characters" })
      .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
      .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
      .regex(/[0-9]/, { message: "Password must contain at least one number" })
      .regex(/[@$!%*?&]/, { message: "Password must contain at least one special character (@$!%*?&)" }),
})

export const resetPasswordValidation = z.object({
  password: z.string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
    .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
    .regex(/[0-9]/, { message: "Password must contain at least one number" })
    .regex(/[@$!%*?&]/, { message: "Password must contain at least one special character (@$!%*?&)" }),
  confirmPassword: z.string().min(8, { message: "Confirm password must be at least 8 characters" }),
}).refine((data) => data.password === data.confirmPassword, {
  path: ["confirmPassword"],
  message: "Passwords do not match",
})

export const changeMyPasswordValidation = z.object({
  oldPassword: z.string().min(8, { message: "Old password must be at least 8 characters" }),
  password: z.string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
    .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
    .regex(/[0-9]/, { message: "Password must contain at least one number" })
    .regex(/[@$!%*?&]/, { message: "Password must contain at least one special character (@$!%*?&)" }),
  confirmPassword: z.string().min(8, { message: "Confirm password must be at least 8 characters" }),
}).refine((data) => data.password === data.confirmPassword, {
  path: ["confirmPassword"],
  message: "Passwords do not match",
})

export const productValidation = (isEdit: boolean) => z.object({
  nameEn: z.string().min(1, { message: "Name in English is required" }),
  nameAr: z.string().min(1, { message: "Name in Arabic is required" }),
  descriptionEn: z.string().min(1, { message: "Description in English is required" }),
  descriptionAr: z.string().min(1, { message: "Description in Arabic is required" }),
  category: z.string().min(1, { message: "Category is required" }),
  minQuantity: z.number().min(0, { message: "Minimum quantity must be greater than 0" }).optional(),
  basedFeature: z.string().optional().nullable(),
  storable: z.number().min(0).max(1).optional(),
  hidden: z.number().min(0).max(1).optional(),
  productPrices: z.array(z.object({
    id: z.number().optional(),
    price: z.number().min(0, { message: "Price must be greater than 0" }),
    // remainingQuantity: z.number().min(0, { message: "Quantity must be greater than 0" }).optional(),
    basedFeatureValue: z.string().optional(),
    basedFeatureValueAr: z.string().optional(),
    featureDependentPrice: z.boolean(),
    images: isEdit
      ? z.array(z.any()).optional()
      : z.array(z.instanceof(File)).min(1, { message: "At least one image is required" }),
    features: z.array(z.object({
      id: z.any().optional(),
      feature: z.string(),
      value: z.string().optional(),
      dependChoiceFeatureId: z.string().optional(),
      dependChoiceId: z.string().optional(),
      choices: z.array(z.object({
        id: z.any().optional(),
        nameEn: z.string().min(1, { message: "Name in English is required" }),
        nameAr: z.string().min(1, { message: "Name in Arabic is required" }),
        price: z.number().min(0, { message: "Price must be greater than 0" })
      })).optional()
    })).optional()
  }))
});


export const ticketValidation = z.object({
  content: z.string().min(1, { message: "Content is required" }),
});


export const bannerValidation = (isEdit: boolean) => z.object({
  image: isEdit
    ? z.array(z.instanceof(File)).optional()
    : z.array(z.instanceof(File)).min(1, { message: "Image is required" }),
});

export const paymentLinkValidation = z.object({
  amount: z.number({ invalid_type_error: "Amount is required" }).positive({ message: "Amount is required" }),
  description: z.string().optional(),
  customerName: z.string().min(2, { message: "Customer name is required" }),
  // Optional, but still validated when actually filled in.
  customerPhone: z.string().min(5, { message: "Phone number is required" }).optional().or(z.literal("")),
  customerEmail: z.string().email({ message: "Invalid email address" }).optional().or(z.literal("")),
}).refine((data) => !!data.customerPhone || !!data.customerEmail, {
  // Tap error 1139: a charge needs at least one to reach the customer. Attached to
  // customerPhone so the message lands under a field that's actually on screen.
  message: "Enter a phone number or an email address",
  path: ["customerPhone"],
});

export const companyInfoValidation = z.object({
  youtube: z.string().optional(),
  linkedin: z.string().optional(),
  facebook: z.string().optional(),
  instagram: z.string().optional(),
  whatsapp: z.string().optional(),
  twitter: z.string().optional(),
  longitude: z.number().optional(),
  latitude: z.number().optional(),
  phones: z.string().optional(),
  email: z.string().optional(),
  locationEn: z.string().optional(),
  locationAr: z.string().optional(),
});
